import { useCallback } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  combineRunProps,
  type OverrideRunProps,
  useIdempotencyErrorHandling,
  parseErrorJson,
  parseErrorResponse,
  type ErrorResponse,
  type AnyException,
  type Data,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { BulkPaymentForm } from "../bulkPayments/BulkPayment.form";
import { getScreenWithError } from "../bulkPayments/helpers";
import { useAppDispatch } from "../utils";
import { useInstitution } from "../institution";

import { API } from "./api";
import { BulkTemplateForm } from "./BulkTemplate.form";
import { formatFormValues, formatFromTemplatePayload } from "./helpers";
import { actions } from "./slice";

import type {
  FormattedBulkTemplate,
  TemplateActivePaymentsCounts,
} from "./types";

export const useCreateBulkTemplate = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateBulkTemplate.Response,
    API.CreateBulkTemplate.Error
  >();
  const { values } = BulkTemplateForm.useForm();
  const institution = useInstitution();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
  } = institution?.features ?? {};

  const createBulkTemplate = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.CreateBulkTemplate.Response,
        API.CreateBulkTemplate.Error
      > = {},
    ) => {
      const payload = formatFormValues(values, {
        expeditedPaymentsFeature: Boolean(expeditedPaymentsFeature),
        achPaymentsCollectFeature: Boolean(achPaymentsCollectFeature),
      });

      const runProps = combineRunProps(
        {
          onSuccess: (data: API.BulkTemplate) => {
            const isPendingApproval = data.state === "pending_approval";
            throwToast({
              kind: "success",
              message: isPendingApproval
                ? l10n.getString(
                    "bulk-template-submitted-for-approval",
                    null,
                    "Template submitted for approval.",
                  )
                : l10n.getString(
                    "bulk-template-created-success",
                    null,
                    "Template created successfully.",
                  ),
            });
          },
          onError: async (error: ErrorResponse<AnyException<Data>>) => {
            const { errors } = await parseErrorResponse(error);
            throwToast({
              kind: "error",
              message: errors.length
                ? `${errors[0].description}`
                : l10n.getString(
                    "bulk-template-create-error",
                    null,
                    "We could not create your template. Please try again.",
                  ),
            });
          },
        },
        overrideRunProps,
      );

      send({
        action: API.createBulkTemplate(payload),
        ...runProps,
      });
    },
    [
      send,
      values,
      expeditedPaymentsFeature,
      achPaymentsCollectFeature,
      throwToast,
      l10n,
    ],
  );

  return useObjectMemo({
    error: status === "error",
    createBulkTemplate,
    loading,
  });
};

export const useGetTemplate = () => {
  const { send, loading } = useRequestWithFeedback<
    API.GetBulkTemplate.Response,
    API.GetBulkTemplate.Error
  >();

  const getTemplate = useCallback(
    (
      templateId: API.BulkTemplateId,
      options?: {
        onData?: (data: API.BulkTemplate) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.getTemplate(templateId),
        onData: options?.onData,
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    getTemplate,
    loading,
  });
};

export const useGetTemplateVersions = () => {
  const { send, loading } = useRequestWithFeedback<
    API.GetBulkTemplateVersions.Response,
    API.GetBulkTemplateVersions.Error
  >();

  const getTemplateVersions = useCallback(
    (
      groupId: API.BulkTemplateId,
      options?: {
        onData?: (data: API.BulkTemplateVersion[]) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.getTemplateVersions(groupId),
        onData: (response) => options?.onData?.(response.results),
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    getTemplateVersions,
    loading,
  });
};

export const useGetBulkTemplates = () => {
  const { send, loading } = useRequestWithFeedback<
    API.GetBulkTemplates.Response,
    API.GetBulkTemplates.Error
  >();

  const getBulkTemplates = useCallback(
    (options?: {
      onData?: (data: API.BulkTemplate[]) => void;
      onError?: (error: unknown) => void;
    }) => {
      return send({
        action: API.getBulkTemplates(),
        onData: ({ results }) => options?.onData?.(results),
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    getBulkTemplates,
    loading,
  });
};

export const useGetTemplateVersion = () => {
  const { send, loading } = useRequestWithFeedback<
    API.GetBulkTemplateVersion.Response,
    API.GetBulkTemplateVersion.Error
  >();

  const getTemplateVersion = useCallback(
    (
      groupId: API.BulkTemplateId,
      versionId: API.BulkTemplateVersionId,
      options?: {
        onData?: (data: API.BulkTemplateVersion) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.getTemplateVersion(groupId, versionId),
        onData: options?.onData,
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    getTemplateVersion,
    loading,
  });
};

export const useGetTemplateVersionById = () => {
  const { send, loading } = useRequestWithFeedback<
    API.GetBulkTemplateVersion.Response,
    API.GetBulkTemplateVersion.Error
  >();

  const getTemplateVersionById = useCallback(
    (
      versionId: API.BulkTemplateVersionId,
      options?: {
        onData?: (data: API.BulkTemplateVersion) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.getTemplateVersionById(versionId),
        onData: options?.onData,
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    getTemplateVersionById,
    loading,
  });
};

export const useCreateTemplateVersion = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateTemplateVersion.Response,
    API.CreateTemplateVersion.Error
  >();
  const { values } = BulkTemplateForm.useForm();
  const institution = useInstitution();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
  } = institution?.features ?? {};

  const createTemplateVersion = useCallback(
    (
      groupId: API.BulkTemplateId,
      versionCount: number,
      overrideRunProps: OverrideRunProps<
        API.CreateTemplateVersion.Response,
        API.CreateTemplateVersion.Error
      > = {},
    ) => {
      const { name, version } = formatFormValues(values, {
        expeditedPaymentsFeature: Boolean(expeditedPaymentsFeature),
        achPaymentsCollectFeature: Boolean(achPaymentsCollectFeature),
      });
      const changeLogNote = values.changeLog?.trim();

      const payload: API.CreateTemplateVersion.Request = {
        ...version,
        name,
        expected_version_number: versionCount + 1,
        ...(changeLogNote && { user_change_log: changeLogNote }),
      };

      const runProps = combineRunProps(
        {
          onSuccess: (data: API.BulkTemplateVersion) => {
            const isPendingApproval =
              data.dual_approval_state === "awaiting_approval";
            throwToast({
              kind: "success",
              message: isPendingApproval
                ? l10n.getString(
                    "bulk-template-version-submitted-for-approval",
                    null,
                    "Version submitted for approval.",
                  )
                : l10n.getString(
                    "bulk-template-version-updated-success",
                    null,
                    "Version updated successfully.",
                  ),
            });
          },
          onError: async (error: ErrorResponse<AnyException<Data>>) => {
            const { errors } = await parseErrorResponse(error);
            throwToast({
              kind: "error",
              message: errors.length
                ? `${errors[0].description}`
                : l10n.getString(
                    "bulk-template-version-update-error",
                    null,
                    "We could not update your template. Please try again.",
                  ),
            });
          },
        },
        overrideRunProps,
      );

      send({
        action: API.createTemplateVersion(groupId, payload),
        ...runProps,
      });
    },
    [
      send,
      values,
      expeditedPaymentsFeature,
      achPaymentsCollectFeature,
      throwToast,
      l10n,
    ],
  );

  return useObjectMemo({
    error: status === "error",
    createTemplateVersion,
    loading,
  });
};

export const useArchiveTemplate = () => {
  const { send, loading } = useRequestWithFeedback();

  const archiveTemplate = useCallback(
    (
      templateId: API.BulkTemplateId,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.archiveTemplate(templateId),
        onSuccess: options?.onSuccess,
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    archiveTemplate,
    loading,
  });
};

export const useTemplateActivePaymentsCounts = () => {
  const { send, loading } = useRequestWithFeedback<{
    page: { totalElements: number };
    results: API.BulkPayment[];
  }>();

  const fetchActivePaymentsCounts = useCallback(
    (
      templateId: API.BulkTemplateId,
      options?: {
        onData?: (counts: TemplateActivePaymentsCounts) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      return send({
        action: API.getTemplateActivePayments(templateId),
        onData: (response) => {
          const pending = response.results.filter(
            (bp) => bp.state === "awaiting_approval",
          );
          const scheduled = response.results.filter(
            (bp) => bp.state !== "awaiting_approval",
          );

          options?.onData?.({
            scheduledBulkCount: scheduled.length,
            scheduledIndividualCount: scheduled.reduce(
              (sum, bp) => sum + bp.payment_count,
              0,
            ),
            pendingBulkCount: pending.length,
            pendingIndividualCount: pending.reduce(
              (sum, bp) => sum + bp.payment_count,
              0,
            ),
          });
        },
        onError: options?.onError,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchActivePaymentsCounts,
    loading,
  });
};

export const useFetchPaginatedBulkTemplates = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.GetBulkTemplates.Response,
    API.GetBulkTemplates.Error
  >();

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (
      pageNumber: number,
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
    ) => {
      send({
        action: API.getBulkTemplates(offsetIndex, pageSize, queryString),
        onData: ({ results, page: { totalElements } }) => {
          dispatch(
            actions.upsertPage({
              pageNumber,
              results,
              total: totalElements,
            }),
          );
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPage,
    loading,
  });
};

export const useCreateBulkPaymentFromTemplate = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateBulkPaymentFromTemplate.Response,
    API.CreateBulkPaymentFromTemplate.Error
  >();
  const { values } = BulkPaymentForm.useForm();
  const institution = useInstitution();
  const { expedited_payments: expeditedPaymentsFeature } =
    institution?.features ?? {};
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createBulkPaymentFromTemplate = useCallback(
    (
      idempotencyKey: UUID,
      {
        template,
        excludedRows,
      }: {
        template: FormattedBulkTemplate;
        excludedRows: Set<number>;
      },
      overrideRunProps: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
      } = {},
      deviceId?: string,
      deviceRequestId?: string,
    ) => {
      const { title, description, secCode, entryDesc, payments } = values;

      const payload = formatFromTemplatePayload({
        template,
        title,
        description: description ?? "",
        secCode,
        entryDesc,
        allPayments: payments,
        excludedRows,
        expeditedPaymentsFeature: Boolean(expeditedPaymentsFeature),
        deviceId,
        deviceRequestId,
      });

      send({
        action: API.createBulkPaymentFromTemplate(idempotencyKey, payload),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          const screenWithError = getScreenWithError(json);
          overrideRunProps.onError?.(screenWithError);
        },
      });
    },
    [send, values, expeditedPaymentsFeature, canHandleIdempotencyErrors],
  );

  return useObjectMemo({
    error: status === "error",
    createBulkPaymentFromTemplate,
    loading,
  });
};
